import DeleteIcon from "@mui/icons-material/Delete";
import { Divider, Grid, Tooltip } from "@mui/material";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Cookies from "js-cookie";
import React from "react";
import { useTranslation } from "react-i18next";
import { ImageInfo } from "../../../../types/junoTypes";
import { localDate } from "../../../../utils/utils";
import ActionLabels from "./ActionLabels";
import HistoryActions from "./HistoryActions";
import HistoryImageSlider from "./HistoryImageSlider";
import HistoryParams from "./HistoryParams";

interface DetailViewProps {
  requests: [ImageInfo][];
  activeIndexes: { [request_uuid: string]: number };
  updateActiveIndex: (request_uuid: string, newIndex: number) => void;
  setOpenModal: (open: boolean) => void;
  setSelectedImageUuid: (uuid: string | null) => void;
  handleDialogOpen: (uuid: string) => void;
}

const DetailView: React.FC<DetailViewProps> = ({
  requests,
  activeIndexes,
  updateActiveIndex,
  setOpenModal,
  setSelectedImageUuid,
  handleDialogOpen,
}) => {
  const { t } = useTranslation();
  const uiLanguage = Cookies.get("ui_language") || "en";

  const rowNums = (length: number, action: string | undefined) => {
    if (action === "img2img" || action === "txt2img") {
      return length > 1 ? 3 : 3;
    } else {
      return length > 1 ? 5 : 3;
    }
  };

  return (
    <>
      {requests.map((request, index) => {
        const params = request[0].params;
        return (
          <Box key={index} my={2} px={2}>
            {index !== 0 && <Divider sx={{ my: 3 }} />}
            <Grid container spacing={2} sx={{ display: "flex", flexDirection: "row", height: "100%" }}>
              {/* Slider */}
              <Grid
                size={{
                  md: 2.5,
                  xs: 12,
                }}
              >
                <HistoryImageSlider
                  images={request as ImageInfo[]}
                  activeIndex={activeIndexes[request[0]["request_uuid"]] || 0}
                  setActiveIndex={(newIndex) => updateActiveIndex(request[0]["request_uuid"], newIndex)}
                  setOpenModal={setOpenModal}
                  setSelectedImageUuid={setSelectedImageUuid}
                />
              </Grid>

              {/* 画像情報 */}
              <Grid
                sx={{ display: "flex", height: "100%", flexDirection: "column", gap: 3 }}
                id={`history-${index}`}
                size={{
                  md: 9.5,
                  xs: 12,
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "row", overflow: "auto" }} gap={1}>
                  <ActionLabels params={params} />
                  <Box flexGrow={1} />
                  <Box>
                    <Typography variant={"body2"} component={"p"} sx={{ py: 1, whiteSpace: "nowrap" }}>
                      {request[0]["created_at"] && localDate(request[0]["created_at"], uiLanguage)}
                    </Typography>
                  </Box>
                  <Box>
                    <Tooltip title={t("common.delete")} placement={"top"}>
                      <span>
                        <IconButton
                          color={"error"}
                          disabled={request[0].request_status === "Processing"}
                          onClick={() => handleDialogOpen(request[0].request_uuid)}
                        >
                          <DeleteIcon fontSize={"small"} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>

                {/* プロンプト */}
                <Box sx={{ display: "flex", flexDirection: "row", width: "100%" }} gap={1}>
                  {params["prompt"] && (
                    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                      <TextField
                        label={t("juno.history.prompt")}
                        fullWidth
                        multiline
                        rows={rowNums(request.length, params["action"])}
                        value={params["prompt"]}
                        InputProps={{
                          readOnly: true,
                        }}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </Box>
                  )}

                  {/* ネガティブプロンプト */}
                  {params["negative_prompt"] && (
                    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                      <TextField
                        label={t("juno.input.negativePromptHistory")}
                        fullWidth
                        multiline
                        rows={rowNums(request.length, params["action"])}
                        value={params["negative_prompt"]}
                        InputProps={{ readOnly: true }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Box>
                  )}

                  {/* システムプロンプト */}
                  {params["system_prompt"] && (
                    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                      <TextField
                        label={t("juno.input.systemPrompt")}
                        fullWidth
                        multiline
                        rows={rowNums(request.length, params["action"])}
                        value={params["system_prompt"]}
                        InputProps={{ readOnly: true }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Box>
                  )}
                </Box>

                {/* 一行に表示するパラメータ */}
                <HistoryParams params={params} grid={2} />

                {/* アクション */}
                <Grid container spacing={1}>
                  {/*<Box flexGrow={1}/>*/}
                  <HistoryActions
                    params={params}
                    grid={2}
                    request={request}
                    targetIndex={activeIndexes[request[0]["request_uuid"]] ?? 0}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Box>
        );
      })}
    </>
  );
};

export default DetailView;
